import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SitePerformanceResponse {
  success: boolean;
  messages?: string[];
  downloadTimeSeconds?: number;
  downloadTimeMs?: number;
  data?: any;
}

export interface CameraTestResponse {
  success: boolean;
  testResults?: {
    zenTicketUrl?: string;
    connectivityTests?: {
      httpConnectivity?: {
        success: boolean;
        responseTimeMs: number;
        testUrl: string;
        message: string;
        statusCode: number;
        status: string;
      };
      socketConnectivity?: {
        port: number;
        success: boolean;
        ipAddress: string;
        responseTimeMs: number;
        message: string;
        status: string;
      };
      responseTimeAnalysis?: {
        averageResponseTimeMs: number;
        maxResponseTimeMs: number;
        overallAssessment: string;
        testCount: number;
        minResponseTimeMs: number;
        success: boolean;
        responseTimeVariance: number;
        successfulTests: number;
        stability: string;
        stabilityMessage: string;
        assessmentMessage: string;
      };
      authentication?: {
        success: boolean;
        hasPassword: boolean;
        message: string;
        hasUsername: boolean;
        status: string;
      };
    };
    ipAddress?: string;
    message?: string;
    installedStatus?: string;
    deviceId?: string;
    deviceName?: string;
    hasCredentials?: boolean;
    recommendations?: string[];
    cameraType?: string;
    assessment?: {
      httpConnectivity: boolean;
      overallHealth: string;
      socketConnectivity: boolean;
      hasOutage: boolean;
      deviceInstalled: boolean;
      timeoutReason?: string;
      deviceOnline: boolean;
      timeoutRisk: string;
      responseTimeGood: boolean;
      healthMessage: string;
      authentication: boolean;
    };
    port?: string;
    zenTicketId?: string;
    status?: string;
  };
  siteName?: string;
  cameraUniqId?: string;
  potentialId?: string;
  cameraName?: string;
  timestamp?: string;
}

const BASE_URLS = {
  test: 'https://test-psafe.pro-vigil.us:8026',
  uat: 'https://pro-safe-sandbox.pro-vigil.info:8026',
  prod: 'https://pro-safe.pro-vigil.info:8026'
};

@Injectable({
  providedIn: 'root'
})
export class PerformanceMonitoringService {
  private baseUrl = BASE_URLS[environment.env] || BASE_URLS.test;

  constructor(private http: HttpClient) {}

  setEnv(env: string) {
    this.baseUrl = BASE_URLS[env] || BASE_URLS.test;
    console.log('Performance Service Base URL:', this.baseUrl);
  }

  /**
   * Get site-level performance for a specific potential ID
   */
  getSitePerformance(potentialId: string): Observable<SitePerformanceResponse> {
    const url = `${this.baseUrl}/api/hd/site/performance/${potentialId}`;
    console.log('Calling Site Performance URL:', url);
    return this.http.get<SitePerformanceResponse>(url);
  }

  /**
   * Get camera-level performance test for a specific camera
   */
  getCameraPerformance(potentialId: string, cameraUniqId: string): Observable<CameraTestResponse> {
    const url = `${this.baseUrl}/api/hd/camera/test/${potentialId}/${cameraUniqId}`;
    console.log('Calling Camera Performance URL:', url);
    return this.http.get<CameraTestResponse>(url);
  }

  /**
   * Get performance data for multiple cameras in a job
   */
  getJobPerformanceData(job: any): Observable<{ sitePerformance?: SitePerformanceResponse; cameraPerformances: CameraTestResponse[] }> {
    return new Observable(observer => {
      const results: { sitePerformance?: SitePerformanceResponse; cameraPerformances: CameraTestResponse[] } = {
        cameraPerformances: []
      };

      // Get site performance if potentialId exists
      if (job.potentialId) {
        this.getSitePerformance(job.potentialId).subscribe({
          next: (sitePerf) => {
            results.sitePerformance = sitePerf;
            
            // Get camera performances if camera list exists
            if (job.cameraUniqIdList && job.cameraUniqIdList.length > 0) {
              this.getCameraPerformances(job.potentialId, job.cameraUniqIdList, results, observer);
            } else {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            console.error('Error fetching site performance:', error);
            // Continue with camera performances even if site performance fails
            if (job.cameraUniqIdList && job.cameraUniqIdList.length > 0) {
              this.getCameraPerformances(job.potentialId, job.cameraUniqIdList, results, observer);
            } else {
              observer.next(results);
              observer.complete();
            }
          }
        });
      } else {
        observer.next(results);
        observer.complete();
      }
    });
  }

  private getCameraPerformances(potentialId: string, cameraIds: string[], results: any, observer: any) {
    let completed = 0;
    const total = cameraIds.length;

    cameraIds.forEach(cameraId => {
      this.getCameraPerformance(potentialId, cameraId).subscribe({
        next: (cameraPerf) => {
          results.cameraPerformances.push(cameraPerf);
          completed++;
          
          if (completed === total) {
            observer.next(results);
            observer.complete();
          }
        },
        error: (error) => {
          console.error(`Error fetching camera performance for ${cameraId}:`, error);
          completed++;
          
          if (completed === total) {
            observer.next(results);
            observer.complete();
          }
        }
      });
    });
  }
} 